import { useMemo, useRef, useEffect, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { Maybe } from '@graphql/generated';
import { addError } from '@shared/utils/logger';
import { isValidCoordinates } from '@shared/core/places/location';
import type { AutoPopulationMode, AutoPopulationPollingStatus, BaseStartAutoPopulationParams, UseAutoPopulationPollingReturn } from './useAutoPopulation.types';

const OPERATION_NAME = 'autoPopulation';

/**
 * API response shape expected by the hook.
 */
interface ApiResponse
  extends Readonly<{
    error?: Maybe<string>;
  }> {}

/**
 * API data shape - the result of calling API hooks at the consumer level.
 * This replaces the unsafe pattern of passing hook-calling functions.
 */
export interface ApiData<TApiPayload>
  extends Readonly<{
    callApi: (payload: TApiPayload) => Promise<ApiResponse>;
    loading: boolean;
  }> {}

/**
 * Options for the auto-population hook.
 */
export interface UseAutoPopulationOptions<TResult, TApiPayload, TStartParams extends BaseStartAutoPopulationParams, TContext>
  extends Readonly<{
    /** Mode of operation for auto-population */
    mode: AutoPopulationMode;

    /** Callback to be called when auto-population is completed */
    onComplete?: (results: ReadonlyArray<TResult>) => void;

    /** Context for error logging */
    context: TContext;

    /**
     * API hook results - call your API hook at the top level and pass results here.
     */
    api: ApiData<TApiPayload>;

    /**
     * Polling hook results - call your polling hook at the top level and pass results here.
     */
    polling: UseAutoPopulationPollingReturn<TResult>;

    /** Map start params to API payload */
    mapApiParams: (params: TStartParams, eventId: string, context: TContext) => TApiPayload;
  }> {}

/**
 * Return value for the auto-population hook.
 */
export interface UseAutoPopulationReturn<TResult, TStartParams extends BaseStartAutoPopulationParams>
  extends Readonly<{
    /** Start auto-population with the given parameters */
    startAutoPopulation: (params: TStartParams) => Promise<void>;

    /** Restart polling without firing the API mutation again (useful when another caller already started the job) */
    restartPolling: () => void;

    /** Current status of auto-population */
    status: AutoPopulationPollingStatus;

    /** Results of auto-population */
    results: ReadonlyArray<TResult>;

    /** Error message if auto-population failed */
    error: Maybe<string>;
  }> {}

/**
 * Hook for auto-population that handles the common orchestration logic.
 *
 * IMPORTANT: This hook accepts pre-computed results from API and polling hooks.
 * Consumers MUST call their hooks at the top level and pass the results here.
 * This ensures React's Rules of Hooks are followed and hook call order is stable.
 */
export const useAutoPopulation = <TResult, TApiPayload, TStartParams extends BaseStartAutoPopulationParams, TContext>(
  options: UseAutoPopulationOptions<TResult, TApiPayload, TStartParams, TContext>,
): UseAutoPopulationReturn<TResult, TStartParams> => {
  const { mode = 'auto', onComplete, context, api, polling, mapApiParams } = options;

  const [isStarting, setIsStarting] = useState(false);

  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  // Prevents duplicate onComplete callbacks when polling updates and effects trigger simultaneously
  const hasCompletedRef = useRef(false);
  const currentEventIdRef = useRef<Maybe<string>>(eventId ?? null);
  // Guards against firing onComplete from stale cached COMPLETED data on mount/remount.
  // Set to true via two paths — both indicate a real completion in this session:
  //   1. startAutoPopulation() was explicitly called (covers fast completions with no PENDING).
  //   2. Polling observed PENDING (covers server-side triggers like new-wedding auto-population
  //      where the UI never calls startAutoPopulation directly).
  // On a fresh mount this starts false, so remount + already-COMPLETED cache never fires.
  const hasSessionActivityRef = useRef(false);

  // API and polling data are passed in from hooks called at the consumer level
  const { callApi, loading: loadingApi } = api;
  const { status: pollStatus, results: pollResults, loading: loadingPoll, queryLoading: pollingQueryLoading, error: pollError, restartPolling } = polling;

  // Used for status derivation - polling is skipped in start-only mode or when no eventId
  const shouldSkipPolling = mode === 'start-only' || !eventId;

  // Reset state when event changes to prevent stale data
  useEffect(() => {
    const newEventId = eventId ?? null;
    if (currentEventIdRef.current !== newEventId) {
      currentEventIdRef.current = newEventId;
      hasCompletedRef.current = false;
      hasSessionActivityRef.current = false;
      setIsStarting(false);
    }
  }, [eventId]);

  // Track session activity via any path:
  //   1. startAutoPopulation() explicitly called (sets hasSessionActivityRef directly).
  //   2. Polling observed PENDING status.
  //   3. Polling is actively fetching (loadingPoll) — covers server-triggered jobs
  //      that complete before PENDING is ever observed.
  useEffect(() => {
    if (pollStatus === 'PENDING' || loadingPoll) {
      hasSessionActivityRef.current = true;
    }
  }, [pollStatus, loadingPoll]);

  // Trigger onComplete callback when polling completes successfully
  useEffect(() => {
    if (hasCompletedRef.current || mode === 'start-only' || isStarting) return;
    if (pollStatus === 'COMPLETED' && pollResults.length > 0 && onComplete && hasSessionActivityRef.current) {
      hasCompletedRef.current = true;
      onComplete(pollResults);
    }
  }, [pollStatus, pollResults, mode, onComplete, isStarting]);

  // Combine loading states — include pollingQueryLoading to prevent false COMPLETED
  // when the Apollo query hasn't resolved yet (no operations ≠ truly completed).
  const isLoading = loadingApi || loadingPoll || pollingQueryLoading;

  // Derive unified status
  const status = useMemo(() => {
    if (isLoading || isStarting) return 'PENDING';
    if (shouldSkipPolling && mode === 'poll-only') return 'COMPLETED';
    return pollStatus;
  }, [isLoading, isStarting, pollStatus, shouldSkipPolling, mode]);

  // Start auto-population: validates params, calls API, handles results
  const startAutoPopulation = useEventCallback(async (params: TStartParams) => {
    if (isStarting) return;

    const targetEventId = params.eventId ?? eventId;
    if (!targetEventId) {
      addError(new Error('Missing event id'), { context, operation: OPERATION_NAME });
      return;
    }

    const { venue } = params;

    if (!isValidCoordinates(venue)) {
      addError(new Error('Missing or invalid location coordinates'), { context, eventId: targetEventId, operation: OPERATION_NAME });
      return;
    }

    setIsStarting(true);
    hasCompletedRef.current = false;
    hasSessionActivityRef.current = true;

    try {
      const apiPayload = mapApiParams(params, targetEventId, context);
      const response = await callApi(apiPayload);

      if (response?.error) {
        addError(new Error(response.error), { context, eventId: targetEventId, isAsync: true, operation: 'response' });
        return;
      }

      // Restart polling to fetch async job results; 'start-only' mode skips this
      if (mode === 'auto') {
        restartPolling();
      }
    } catch (error) {
      addError(error instanceof Error ? error : new Error('Auto-population failed'), { context, eventId: targetEventId, operation: OPERATION_NAME });
    } finally {
      setIsStarting(false);
    }
  });

  return {
    startAutoPopulation,
    restartPolling,
    status,
    results: pollResults,
    error: pollError,
  };
};
